<script setup>
import { ref, computed } from "vue";
import { useScrollReveal } from "../../composables/useScrollReveal";
import { useGsap } from "../../composables/useGsap";

const props = defineProps({
  // Accepted but not drawn: a label above every section title read as
  // template chrome. The title says what the section is.
  eyebrow: { type: String, default: "" },
  title: { type: String, required: true },
  subtitle: { type: String, default: "" },
  align: { type: String, default: "center" }, // center | start
  level: { type: String, default: "h2" },
});

const root = ref(null);
const { revealed } = useScrollReveal(root);

// The title a word at a time, for the rise below.
const words = computed(() => props.title.split(" "));

useGsap(root, (gsap) => {
  gsap.from(".word__inner", {
    yPercent: 115,
    duration: 0.9,
    ease: "expo.out",
    stagger: 0.07,
    scrollTrigger: { trigger: root.value, start: "top 88%", once: true },
  });
});
</script>

<template>
  <header
    ref="root"
    class="heading reveal"
    :class="[`heading--${align}`, { 'is-revealed': revealed }]"
  >
    <component :is="level" class="heading__title">
      <span class="visually-hidden">{{ title }}</span>
      <!-- Split for the rise; the whole title above is what gets read. -->
      <span aria-hidden="true">
        <template v-for="(word, i) in words" :key="i">
          <span class="word"><span class="word__inner">{{ word }}</span></span>{{ i < words.length - 1 ? " " : "" }}
        </template>
      </span>
    </component>
    <p v-if="subtitle" class="heading__subtitle lead">{{ subtitle }}</p>
  </header>
</template>

<style scoped>
.heading {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-block-end: var(--space-8);
}

.heading--center {
  align-items: center;
  text-align: center;
}

.heading--center .heading__subtitle {
  max-inline-size: 58ch;
}

.heading--start {
  align-items: flex-start;
  text-align: start;
}

.heading__subtitle {
  margin: 0;
}
</style>
